import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Dimensions,
  Image,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useAppDispatch } from '../../store';
import { submitCategoryFeedback } from '../../store/slices/intelligentSearchSlice';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { AIExplanationDisplay } from './AIExplanationDisplay';
import type {
  PersonalizedCategoryGridProps,
  PersonalizedCategory,
  CategoryFeedback,
} from '@coffeeplexity/shared';

interface CategoryCardProps {
  category: PersonalizedCategory;
  onPress: () => void;
  onFeedback: (feedback: CategoryFeedback) => void;
  showSubcategories: boolean;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  onPress,
  onFeedback,
  showSubcategories,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const getGradientColors = (color: string): [string, string] => {
    // Create gradient variations based on the category color
    const baseColor = color || '#2196F3';
    const lightColor = baseColor + '80'; // Add transparency
    const darkColor = baseColor + 'CC'; // Darker variant
    return [lightColor, darkColor];
  };

  const handleFeedback = (helpful: boolean, reason?: string) => {
    const feedback: CategoryFeedback = {
      helpful,
      reason,
      timestamp: new Date().toISOString(),
    };
    onFeedback(feedback);
  };

  return (
    <>
      <TouchableOpacity
        style={styles.categoryCard}
        onPress={onPress}
        activeOpacity={0.8}
      >
        <LinearGradient
          colors={getGradientColors(category.color)}
          style={styles.categoryGradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          <View style={styles.categoryHeader}>
            <View style={styles.categoryIconContainer}>
              <Ionicons name={category.icon as any} size={24} color='#fff' />
            </View>

            <TouchableOpacity
              onPress={() => setShowDetails(true)}
              style={styles.infoButton}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons
                name='information-circle-outline'
                size={16}
                color='#fff'
              />
            </TouchableOpacity>
          </View>

          <View style={styles.categoryContent}>
            <Text style={styles.categoryName} numberOfLines={2}>
              {category.displayName}
            </Text>

            <Text style={styles.categoryDescription} numberOfLines={2}>
              {category.description}
            </Text>

            <View style={styles.categoryStats}>
              <View style={styles.statItem}>
                <Ionicons name='library-outline' size={14} color='#fff' />
                <Text style={styles.statText}>
                  {category.contentCount} items
                </Text>
              </View>

              <View style={styles.statItem}>
                <Ionicons name='trending-up-outline' size={14} color='#fff' />
                <Text style={styles.statText}>
                  {Math.round(category.relevanceScore * 100)}% match
                </Text>
              </View>
            </View>

            {showSubcategories && category.subcategories.length > 0 && (
              <View style={styles.subcategoriesContainer}>
                <Text style={styles.subcategoriesTitle}>Topics:</Text>
                <View style={styles.subcategoriesList}>
                  {category.subcategories.slice(0, 3).map((sub, index) => (
                    <Text key={sub.id} style={styles.subcategoryTag}>
                      {sub.name}
                      {index < Math.min(category.subcategories.length, 3) - 1 &&
                        ' • '}
                    </Text>
                  ))}
                  {category.subcategories.length > 3 && (
                    <Text style={styles.subcategoryTag}>
                      +{category.subcategories.length - 3} more
                    </Text>
                  )}
                </View>
              </View>
            )}
          </View>

          <View style={styles.categoryFooter}>
            <ConfidenceIndicator
              confidence={category.skillAlignment}
              size='small'
              showLabel={false}
              color='#fff'
            />

            {category.trendingScore > 0.7 && (
              <View style={styles.trendingBadge}>
                <Ionicons name='flame' size={12} color='#FF5722' />
                <Text style={styles.trendingText}>Trending</Text>
              </View>
            )}
          </View>
        </LinearGradient>
      </TouchableOpacity>

      {/* Category Details Modal */}
      <Modal
        visible={showDetails}
        transparent
        animationType='fade'
        onRequestClose={() => setShowDetails(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{category.displayName}</Text>
              <TouchableOpacity
                onPress={() => setShowDetails(false)}
                style={styles.modalCloseButton}
              >
                <Ionicons name='close' size={24} color='#666' />
              </TouchableOpacity>
            </View>

            <AIExplanationDisplay
              explanation={{
                title: 'Why this category was suggested',
                description: category.metadata.generatedReason,
                confidence: category.relevanceScore,
                factors: [
                  `${Math.round(category.userInterest * 100)}% user interest`,
                  `${Math.round(category.masteryGap * 100)}% skill gap alignment`,
                  `${Math.round(category.recentActivity * 100)}% recent activity`,
                ],
              }}
            />

            <View style={styles.categoryDetailsStats}>
              <View style={styles.detailStatItem}>
                <Text style={styles.detailStatLabel}>Content Count</Text>
                <Text style={styles.detailStatValue}>
                  {category.contentCount}
                </Text>
              </View>
              <View style={styles.detailStatItem}>
                <Text style={styles.detailStatLabel}>Avg. Difficulty</Text>
                <Text style={styles.detailStatValue}>
                  {category.averageDifficulty.toFixed(1)}/10
                </Text>
              </View>
              <View style={styles.detailStatItem}>
                <Text style={styles.detailStatLabel}>Skill Alignment</Text>
                <Text style={styles.detailStatValue}>
                  {Math.round(category.skillAlignment * 100)}%
                </Text>
              </View>
            </View>

            {category.subcategories.length > 0 && (
              <View style={styles.subcategoriesDetail}>
                <Text style={styles.subcategoriesDetailTitle}>
                  Subcategories
                </Text>
                {category.subcategories.map(sub => (
                  <View key={sub.id} style={styles.subcategoryDetailItem}>
                    <Text style={styles.subcategoryDetailName}>{sub.name}</Text>
                    <Text style={styles.subcategoryDetailCount}>
                      {sub.contentCount} items
                    </Text>
                  </View>
                ))}
              </View>
            )}

            <View style={styles.feedbackSection}>
              <Text style={styles.feedbackTitle}>
                Is this category helpful?
              </Text>
              <View style={styles.feedbackButtons}>
                <TouchableOpacity
                  style={[styles.feedbackButton, styles.feedbackButtonPositive]}
                  onPress={() => {
                    handleFeedback(true, 'Relevant and useful');
                    setShowDetails(false);
                  }}
                >
                  <Ionicons name='thumbs-up' size={16} color='#4CAF50' />
                  <Text style={styles.feedbackButtonText}>Helpful</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.feedbackButton, styles.feedbackButtonNegative]}
                  onPress={() => {
                    handleFeedback(false, 'Not relevant to my interests');
                    setShowDetails(false);
                  }}
                >
                  <Ionicons name='thumbs-down' size={16} color='#F44336' />
                  <Text style={styles.feedbackButtonText}>Not helpful</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export const PersonalizedCategoryGrid: React.FC<
  PersonalizedCategoryGridProps
> = ({
  categories,
  onCategorySelect,
  onCategoryFeedback,
  maxCategories = 8,
  showSubcategories = true,
}) => {
  const dispatch = useAppDispatch();
  const screenWidth = Dimensions.get('window').width;
  const cardWidth = (screenWidth - 48) / 2; // 2 columns with padding

  const handleCategoryFeedback = (
    categoryId: string,
    feedback: CategoryFeedback
  ) => {
    onCategoryFeedback(categoryId, feedback);
    dispatch(
      submitCategoryFeedback({
        categoryId,
        feedback,
        userId: 'current-user', // TODO: Get from auth state
      })
    );
  };

  const renderCategory = ({ item }: { item: PersonalizedCategory }) => (
    <View style={[styles.categoryWrapper, { width: cardWidth }]}>
      <CategoryCard
        category={item}
        onPress={() => onCategorySelect(item)}
        onFeedback={feedback => handleCategoryFeedback(item.id, feedback)}
        showSubcategories={showSubcategories}
      />
    </View>
  );

  const displayCategories = categories
    .sort((a, b) => b.relevanceScore - a.relevanceScore)
    .slice(0, maxCategories);

  if (displayCategories.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Ionicons name='grid-outline' size={48} color='#ccc' />
        <Text style={styles.emptyStateText}>
          No personalized categories available
        </Text>
        <Text style={styles.emptyStateSubtext}>
          Complete more activities to get personalized recommendations
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Personalized for You</Text>
        <Text style={styles.subtitle}>
          Categories based on your interests and skill level
        </Text>
      </View>

      <FlatList
        data={displayCategories}
        renderItem={renderCategory}
        keyExtractor={item => item.id}
        numColumns={2}
        columnWrapperStyle={styles.row}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.grid}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  grid: {
    paddingHorizontal: 16,
  },
  row: {
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  categoryWrapper: {
    // Width set dynamically
  },
  categoryCard: {
    height: 180,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  categoryGradient: {
    flex: 1,
    padding: 16,
    justifyContent: 'space-between',
  },
  categoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  categoryIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoButton: {
    padding: 4,
  },
  categoryContent: {
    flex: 1,
    justifyContent: 'center',
  },
  categoryName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 4,
  },
  categoryDescription: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 8,
  },
  categoryStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.9)',
    marginLeft: 4,
  },
  subcategoriesContainer: {
    marginTop: 4,
  },
  subcategoriesTitle: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 2,
  },
  subcategoriesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  subcategoryTag: {
    fontSize: 9,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  categoryFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  trendingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  trendingText: {
    fontSize: 9,
    color: '#fff',
    marginLeft: 2,
    fontWeight: '600',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#666',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 8,
    textAlign: 'center',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    width: '100%',
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  modalCloseButton: {
    padding: 4,
  },
  categoryDetailsStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginVertical: 16,
    paddingVertical: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  detailStatItem: {
    alignItems: 'center',
  },
  detailStatLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  detailStatValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  subcategoriesDetail: {
    marginVertical: 16,
  },
  subcategoriesDetailTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  subcategoryDetailItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  subcategoryDetailName: {
    fontSize: 14,
    color: '#333',
  },
  subcategoryDetailCount: {
    fontSize: 12,
    color: '#666',
  },
  feedbackSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  feedbackTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  feedbackButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  feedbackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
  },
  feedbackButtonPositive: {
    borderColor: '#4CAF50',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
  },
  feedbackButtonNegative: {
    borderColor: '#F44336',
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
  },
  feedbackButtonText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
    color: '#333',
  },
});

export default PersonalizedCategoryGrid;
